/**
 * @fileoverview Caso de uso de autenticación
 * Implementa la lógica de negocio para autenticación de usuarios
 */

import TokenInfo from '../../domain/value-objects/TokenInfo';

/**
 * @class AuthUseCase
 * @description Caso de uso para gestionar autenticación de usuarios
 * Siguiendo el patrón Application Service / Use Case de Clean Architecture
 */
class AuthUseCase {
  /**
   * @constructor
   * @param {IAuthRepository} authRepository - Repositorio de autenticación
   * @param {Object} logger - Sistema de logging para rastreo de eventos
   */
  constructor(authRepository, logger) {
    this.authRepository = authRepository;
    this.logger = logger || console;
    this.logger.info('[AuthUseCase] Inicializado');
  }

  /**
   * Inicia el proceso de autenticación con un proveedor OAuth
   * @param {string} provider - Proveedor de autenticación (tiendanube, google, etc)
   * @param {Object} options - Opciones adicionales para el flujo OAuth
   * @returns {Promise<string>} URL de redirección para OAuth
   */
  async initiateAuth(provider, options = {}) {
    this.logger.info(`[AuthUseCase] Iniciando autenticación con proveedor: ${provider}`);
    
    try {
      const authUrl = await this.authRepository.getAuthProviderUrl(provider);
      this.logger.info(`[AuthUseCase] URL de autenticación generada: ${authUrl}`);
      return authUrl;
    } catch (error) {
      this.logger.error(`[AuthUseCase] Error al iniciar autenticación: ${error.message}`);
      throw new Error(`No se pudo iniciar el flujo de autenticación: ${error.message}`);
    }
  }

  /**
   * Completa el flujo de autenticación con un token personalizado
   * @param {string} token - Token personalizado (normalmente de Firebase)
   * @returns {Promise<Object>} Información del usuario autenticado
   */
  async completeAuth(token) {
    this.logger.info('[AuthUseCase] Completando autenticación con token');
    
    try {
      const authResult = await this.authRepository.signInWithCustomToken(token);
      
      // Guardar el token en session storage para recuperar en caso de fallas
      this._persistSessionData({
        uid: authResult.user.uid,
        tokenInfo: authResult.tokenInfo.toJSON()
      });
      
      this.logger.info(`[AuthUseCase] Autenticación completada exitosamente para: ${authResult.user.uid}`);
      return authResult;
    } catch (error) {
      this.logger.error(`[AuthUseCase] Error al completar autenticación: ${error.message}`);
      throw new Error(`Fallo en autenticación: ${error.message}`);
    }
  }

  /**
   * Cierra la sesión del usuario actual
   * @returns {Promise<void>}
   */
  async signOut() {
    this.logger.info('[AuthUseCase] Cerrando sesión');
    
    try {
      await this.authRepository.signOut();
      this._clearSessionData();
      this.logger.info('[AuthUseCase] Sesión cerrada exitosamente');
    } catch (error) {
      this.logger.error(`[AuthUseCase] Error al cerrar sesión: ${error.message}`);
      // Limpiar datos de sesión incluso si hay error
      this._clearSessionData();
      throw error;
    }
  }

  /**
   * Establece listener para cambios en estado de autenticación
   * @param {Function} callback - Función a ejecutar cuando cambie el estado
   * @returns {Function} Función para eliminar el listener
   */
  subscribeToAuthChanges(callback) {
    this.logger.info('[AuthUseCase] Configurando suscripción a cambios de autenticación');
    
    return this.authRepository.onAuthStateChanged((user, error) => {
      if (error) {
        this.logger.error(`[AuthUseCase] Error en cambio de estado de auth: ${error.message}`);
      }
      
      if (user) {
        this.logger.info(`[AuthUseCase] Cambio de estado: Usuario autenticado: ${user.uid}`);
      } else {
        this.logger.info('[AuthUseCase] Cambio de estado: No autenticado');
        this._clearSessionData();
      }
      
      callback(user, error);
    });
  }

  /**
   * Obtiene token para llamadas a API
   * @param {boolean} [forceRefresh=false] - Forzar renovación del token
   * @returns {Promise<string>} Token JWT para autorización
   */
  async getApiToken(forceRefresh = false) {
    try {
      // Verificar si el token expirará pronto y forzar refresh
      if (!forceRefresh) {
        const willExpireSoon = await this.authRepository.isTokenExpired(5);
        forceRefresh = willExpireSoon;
      }
      
      this.logger.info(`[AuthUseCase] Obteniendo token compatible para API (forceRefresh: ${forceRefresh})`);
      
      try {
        // Usar el método especializado para obtener tokens compatibles con la API
        if (this.authRepository.getApiCompatibleToken) {
          const apiToken = await this.authRepository.getApiCompatibleToken(forceRefresh);
          if (apiToken) {
            this.logger.info('[AuthUseCase] Token API compatible obtenido exitosamente');
            return apiToken;
          }
        }
      } catch (apiError) {
        this.logger.warn('[AuthUseCase] Error al obtener token API compatible, usando método estándar:', apiError);
      }
      
      // Método original como fallback
      let token;
      if (forceRefresh) {
        token = await this.authRepository.refreshToken();
        this.logger.info('[AuthUseCase] Token refrescado exitosamente');
      } else {
        token = await this.authRepository.getIdToken();
      }
      
      return token;
    } catch (error) {
      this.logger.error(`[AuthUseCase] Error al obtener token API: ${error.message}`);
      
      // Si hay error por token inválido, intentar recuperar de session storage
      const sessionData = this._getSessionData();
      if (sessionData?.tokenInfo?.token) {
        this.logger.warn('[AuthUseCase] Intentando recuperar con token de sesión');
        return sessionData.tokenInfo.token;
      }
      
      throw error;
    }
  }

  /**
   * Verifica estado de autenticación actual
   * @returns {Promise<boolean>} True si hay sesión activa
   */
  async isAuthenticated() {
    return this.authRepository.isAuthenticated();
  }

  /**
   * Actualiza el estado de sesión expirada
   * @param {boolean} status - Estado de la sesión
   */
  setSessionExpired(status) {
    if (status) {
      sessionStorage.setItem('sessionExpired', 'true');
      this.logger.warn('[AuthUseCase] Marcando sesión como expirada');
    } else {
      sessionStorage.removeItem('sessionExpired');
      this.logger.info('[AuthUseCase] Limpiando indicador de sesión expirada');
    }
  }

  /**
   * Verifica si la sesión está marcada como expirada
   * @returns {boolean} True si la sesión está marcada como expirada
   */
  isSessionExpired() {
    return sessionStorage.getItem('sessionExpired') === 'true';
  }

  /**
   * Persiste información de sesión en storage local
   * @param {Object} data - Datos a persistir
   * @private
   */
  _persistSessionData(data) {
    try {
      sessionStorage.setItem('authSession', JSON.stringify({
        ...data,
        timestamp: Date.now()
      }));
    } catch (e) {
      this.logger.error('[AuthUseCase] Error al persistir datos de sesión:', e);
    }
  }

  /**
   * Recupera información de sesión de storage local
   * @returns {Object|null} Datos de sesión o null si no existen
   * @private
   */
  _getSessionData() {
    try {
      const data = sessionStorage.getItem('authSession');
      return data ? JSON.parse(data) : null;
    } catch (e) {
      this.logger.error('[AuthUseCase] Error al recuperar datos de sesión:', e);
      return null;
    }
  }

  /**
   * Elimina información de sesión de storage local
   * @private
   */
  _clearSessionData() {
    try {
      sessionStorage.removeItem('authSession');
      sessionStorage.removeItem('sessionExpired');
    } catch (e) {
      this.logger.error('[AuthUseCase] Error al limpiar datos de sesión:', e);
    }
  }
}

export default AuthUseCase;
